import { styled } from '@abyss/web/tools/styled';
import { Alert } from '@abyss/web/ui/Alert';
import { I18nProvider } from '@abyss/web/ui/I18nProvider/I18nProvider';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import find from 'lodash/find';
import React, { useContext, useState } from 'react';

import { PortalContext } from '../../context/PortalContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { isLawwMember } from '../Utils/memberUtils/memberUtils';
import { ConstantsLagoon } from '../ConstantsLagoon';

const LAWWBackgroundWrapper = styled('div', {
  backgroundColor: '$lightBlue',
  width: '100%',
});

const LAWWContentWrapper = styled('div', {
  maxWidth: '1200px',
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: '0',

  '@media (max-width: 1248px)': {
    marginLeft: '24px',
    marginRight: '24px',
  },
  '@screen < $md': {
    marginLeft: '16px',
    marginRight: '16px',
  },
});

const MessageAlertContainer = styled(Alert, {
  '&.abyss-alert-root': {
    backgroundColor: '$lightBlue',
    border: 'none !important',
    boxShadow: 'none !important',
    outline: 'none !important',
    margin: '0 !important',
    '.abyss-alert-container': {
      backgroundColor: '$lightBlue',
      border: 'none !important',
      boxShadow: 'none !important',
      margin: '0 !important',
      padding: '16px !important',
      '.abyss-alert-icon-container': {
        marginRight: '$sm',
        marginTop: '4px',
        height: '50px',
        '& svg': {
          color: '$uhcPrimary1',
        },
      },
      '.abyss-alert-close-button': {
        marginTop: '4px',
        color: '$uhcPrimary1',
      },
      '.abyss-alert-title': {
        color: '$uhcPrimary1',
        fontFamily: '$heading',
        fontWeight: '$semibold',
      },
      '.abyss-alert-content': {
        color: '$uhcPrimary1',
      },
    },
  },
});

const IconWrapper = styled('span', {
  backgroundColor: '$uhcPrimary1',
  padding: '10px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '$md',
});

export const CustomMessageNotificationsLaww = () => {
  const [isMessageDismissed, setIsMessageDismissed] = useState(false);

  const { portalData } = useContext(PortalContext);

  const customMesssgeAll = useLagoon('custom-messages')();

  const customMessageNotificationLaww = find(customMesssgeAll, {
    policyNumber: portalData?.portalConfig?.accessCode,
    active: true,
  });

  const [isLawwMessageEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CUSTOM_MESSAGE_NOTIFICATION_LAWW,
  ]);

  const shouldShowMessage =
    isLawwMember(portalData?.portalName) &&
    isLawwMessageEnabled &&
    !isMessageDismissed &&
    customMessageNotificationLaww?.message;

  const handleMessageClose = () => {
    setIsMessageDismissed(true);
  };

  if (!shouldShowMessage) {
    return null;
  }

  const { title, message, link, linkText } =
    customMessageNotificationLaww || {};

  return (
    <React.Fragment>
      <LAWWBackgroundWrapper>
        <LAWWContentWrapper>
          <I18nProvider translations={{ close: 'Close message' }}>
            <MessageAlertContainer
              data-auto-testid={`laww-custom-message-container`}
              data-testid={`laww-custom-message-container`}
              icon={
                <IconWrapper>
                  <IconMaterial color="white" icon="info" size="30" />
                </IconWrapper>
              }
              onClose={handleMessageClose}
              title={title}
              variant="info"
            >
              <Layout.Stack alignItems="left">
                <span dangerouslySetInnerHTML={{ __html: message }} />

                {linkText && link && (
                  <a
                    data-auto-testid="laww-custom-message-link"
                    data-testid="laww-custom-message-link"
                    href={link}
                    target="_blank"
                  >
                    {linkText}
                  </a>
                )}
              </Layout.Stack>
            </MessageAlertContainer>
          </I18nProvider>
        </LAWWContentWrapper>
      </LAWWBackgroundWrapper>
    </React.Fragment>
  );
};
